import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Lock, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { sanitizeHome } from '@/lib/navigation';
import { passwordPolicyError } from '@/lib/passwordChange';

/**
 * Troca da propria senha. Obrigatoria quando user.must_change_password
 * (ex.: apos "Resetar senha" feito por admin) — o guard de rota do App.js
 * manda para ca e o backend devolve 403 PASSWORD_CHANGE_REQUIRED no resto.
 */
const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const { user, changePassword, logout, mustChangePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const policy = passwordPolicyError(newPassword);
    if (policy) {
      setError(policy);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('As senhas não conferem');
      return;
    }
    if (newPassword === currentPassword) {
      setError('A nova senha deve ser diferente da atual');
      return;
    }

    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Senha alterada com sucesso');
      navigate(sanitizeHome(user?.home_page) || '/', { replace: true });
    } catch (err) {
      const message = err.response?.data?.detail;
      setError(typeof message === 'string' ? message : 'Não foi possível alterar a senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(2, 6, 23, 0.8), rgba(2, 6, 23, 0.95)), url('/assets/octopus-background.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <CardTitle>{mustChangePassword ? 'Defina uma nova senha' : 'Alterar senha'}</CardTitle>
          <CardDescription>
            {mustChangePassword
              ? 'Sua senha foi redefinida por um administrador. Para continuar, crie uma nova senha.'
              : 'Informe a senha atual e a nova senha.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
            <div className="space-y-2">
              <Label htmlFor="current-password">Senha atual</Label>
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova senha</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                data-testid="new-password-input"
              />
              <p className="text-xs text-muted-foreground">
                Mínimo de 8 caracteres, com letras e números. Não pode repetir a atual nem as 5 últimas.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar nova senha</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="confirm-password-input"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" role="alert" data-testid="change-password-error">{error}</p>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="change-password-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar nova senha'}
            </Button>
            <Button type="button" variant="ghost" className="w-full gap-2" onClick={logout} data-testid="change-password-logout">
              <LogOut className="w-4 h-4" /> Sair
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChangePasswordPage;
